// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * touch.ts — the thumb scheme. A RAIL DRAG, never a D-pad.
 *
 * Principle #19: the control is chosen for the game. A stacker moves a piece
 * along one axis in discrete steps, so the right gesture is a rail — drag
 * anywhere on the well and the piece steps a column per ~26px of travel, which
 * keeps the thumb in the lower third and never covers the piece it is steering.
 *
 * Gesture vocabulary (thresholds from MOBILE_CONTROLS §2, which are cited
 * defaults rather than guesses):
 *   tap    < 3px, < 250ms          → rotate
 *   drag   promoted past 8px       → rail: one column per STEP_PX, either axis
 *   swipe  >= 50px, > 0.5px/ms, < 250ms, dominant axis
 *            down → hard drop · up → hold
 *   press-and-hold, no movement    → soft drop repeat
 *
 * Every intent is QUEUED, never applied in the handler — the rAF step drains
 * it, which is what keeps input and simulation on the same clock.
 */

const STEP_PX = 26;
const PROMOTE_PX = 8;
const TAP_PX = 3;
const TAP_MS = 250;
const SWIPE_PX = 50;
const SWIPE_VEL = 0.5;
const SWIPE_MS = 250;
const HOLD_START_MS = 260;
const HOLD_REPEAT_MS = 45;

export interface TouchIntents {
  /** Net column steps requested. */
  moves: number;
  /** Rotation requests (CW). */
  rotates: number;
  /** Soft-drop rows requested. */
  soft: number;
  hard: boolean;
  hold: boolean;
}

export interface TouchLayer {
  drain(): TouchIntents;
  destroy(): void;
  /** True while a finger is down — the HUD dims the hint text. */
  active(): boolean;
}

interface PointerState {
  startX: number;
  startY: number;
  railX: number;
  railY: number;
  emittedX: number;
  emittedY: number;
  startT: number;
  promoted: boolean;
  holdTimer: number | null;
  holdDelay: number | null;
}

const empty = (): TouchIntents => ({ moves: 0, rotates: 0, soft: 0, hard: false, hold: false });

export function attachTouch(el: HTMLElement): TouchLayer {
  let q = empty();
  const pointers = new Map<number, PointerState>();

  const clearHold = (p: PointerState) => {
    if (p.holdTimer !== null) { clearInterval(p.holdTimer); p.holdTimer = null; }
    if (p.holdDelay !== null) { clearTimeout(p.holdDelay); p.holdDelay = null; }
  };

  const onDown = (e: PointerEvent) => {
    if (!e.isPrimary && pointers.size > 0) return;
    // clientX/Y minus the element rect — never offsetX/offsetY, which scales
    // oddly under DPR and page zoom.
    const rect = el.getBoundingClientRect();
    const st: PointerState = {
      startX: e.clientX - rect.left,
      startY: e.clientY - rect.top,
      railX: e.clientX - rect.left,
      railY: e.clientY - rect.top,
      emittedX: 0,
      emittedY: 0,
      startT: e.timeStamp,
      promoted: false,
      holdTimer: null,
      holdDelay: null,
    };
    pointers.set(e.pointerId, st);
    try { el.setPointerCapture(e.pointerId); } catch { /* capture is best-effort */ }
    // Soft-drop repeat starts only AFTER the tap window has closed, or a quick
    // tap-to-rotate would drop a row on its way past.
    st.holdDelay = window.setTimeout(() => {
      st.holdDelay = null;
      if (st.promoted) return;
      st.holdTimer = window.setInterval(() => { if (!st.promoted) q.soft += 1; }, HOLD_REPEAT_MS);
    }, HOLD_START_MS);
    e.preventDefault();
  };

  const onMove = (e: PointerEvent) => {
    const st = pointers.get(e.pointerId);
    if (!st) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const dx = x - st.startX;
    const dy = y - st.startY;
    if (!st.promoted && Math.hypot(dx, dy) >= PROMOTE_PX) {
      st.promoted = true;
      clearHold(st);
      st.railX = x;
      st.railY = y;
    }
    if (!st.promoted) return;

    const wantX = Math.trunc((x - st.railX) / STEP_PX);
    if (wantX !== st.emittedX) { q.moves += wantX - st.emittedX; st.emittedX = wantX; }
    // Downward rail only: dragging up is reserved for the hold gesture.
    const wantY = Math.max(0, Math.trunc((y - st.railY) / STEP_PX));
    if (wantY > st.emittedY) { q.soft += wantY - st.emittedY; st.emittedY = wantY; }
    e.preventDefault();
  };

  const finish = (e: PointerEvent, cancelled: boolean) => {
    const st = pointers.get(e.pointerId);
    if (!st) return;
    clearHold(st);
    pointers.delete(e.pointerId);
    try { el.releasePointerCapture(e.pointerId); } catch { /* already gone */ }
    if (cancelled) return; // pointercancel is an ABORT, never an action

    const rect = el.getBoundingClientRect();
    const dx = e.clientX - rect.left - st.startX;
    const dy = e.clientY - rect.top - st.startY;
    const dist = Math.hypot(dx, dy);
    const dur = Math.max(1, e.timeStamp - st.startT);
    const vel = dist / dur;

    if (dist >= SWIPE_PX && vel > SWIPE_VEL && dur < SWIPE_MS) {
      if (Math.abs(dy) > Math.abs(dx)) {
        if (dy > 0) q.hard = true; else q.hold = true;
        return;
      }
      // A fast horizontal flick is just a long rail move; the steps already
      // went out on pointermove, so there is nothing extra to do.
      return;
    }
    if (!st.promoted && dist < TAP_PX && dur < TAP_MS) q.rotates += 1;
  };

  const onUp = (e: PointerEvent) => finish(e, false);
  const onCancel = (e: PointerEvent) => finish(e, true);

  el.addEventListener('pointerdown', onDown);
  el.addEventListener('pointermove', onMove);
  el.addEventListener('pointerup', onUp);
  el.addEventListener('pointercancel', onCancel);
  el.addEventListener('lostpointercapture', onCancel);

  return {
    drain() { const out = q; q = empty(); return out; },
    active: () => pointers.size > 0,
    destroy() {
      for (const st of pointers.values()) clearHold(st);
      pointers.clear();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onCancel);
      el.removeEventListener('lostpointercapture', onCancel);
    },
  };
}
